import { useEffect, useState, type RefObject } from 'react';

export interface TrackedSelection {
  text: string;
  /** Viewport coordinates of the selection, for positioning a floating action. */
  rect: DOMRect;
}

const MIN_SELECTION_LENGTH = 2;

/**
 * Track text selection inside a container element. Returns the selected text
 * and its bounding rect, or null when there is no usable selection inside
 * the container. Clears on scroll/resize so a floating action never drifts
 * away from its selection.
 */
export function useTextSelection(containerRef: RefObject<HTMLElement | null>) {
  const [selection, setSelection] = useState<TrackedSelection | null>(null);

  useEffect(() => {
    const update = () => {
      const container = containerRef.current;
      const sel = window.getSelection();

      if (!container || !sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSelection(null);
        return;
      }

      const range = sel.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        setSelection(null);
        return;
      }

      const text = sel.toString().trim();
      if (text.length < MIN_SELECTION_LENGTH) {
        setSelection(null);
        return;
      }

      setSelection({ text, rect: range.getBoundingClientRect() });
    };

    const clear = () => setSelection(null);

    // Ignore events originating from the floating highlight action itself,
    // so tapping it doesn't clear the selection before the click lands.
    const guardedUpdate = (event: Event) => {
      if (event.target instanceof Element && event.target.closest('[data-highlight-action]')) {
        return;
      }
      update();
    };

    document.addEventListener('mouseup', guardedUpdate);
    document.addEventListener('keyup', guardedUpdate);
    document.addEventListener('touchend', guardedUpdate);
    document.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);

    return () => {
      document.removeEventListener('mouseup', guardedUpdate);
      document.removeEventListener('keyup', guardedUpdate);
      document.removeEventListener('touchend', guardedUpdate);
      document.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
    };
  }, [containerRef]);

  const clearSelection = () => {
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  };

  return { selection, clearSelection };
}
